import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { Modal } from 'src/components/modals/comps';
import Input, { Textarea } from 'src/components/form/Input';
import Select, { CreatableSelect } from 'src/components/form/Select';
import Button from 'src/components/form/Button';
import { InputLabel, Poppins, Spacer } from 'src/common';
import { showSuccessToast } from 'src/components/Toast';
import {
  IMPORT_FRAMEWORK,
  ImportFrameworkMutation,
  ImportFrameworkMutationVariables,
  UPDATE_FRAMEWORK_METADATA,
  UpdateFrameworkMetadataMutation,
  UpdateFrameworkMetadataMutationVariables,
} from 'src/graphql/contentImport';
import { CsvValidationErrors, SCVSummary } from 'src/components/CsvUpload';
import { parseGraphqlCsvValidationErrors, ValidationErrorItem } from 'src/utils/csv';
import { FrameworkImportPayload } from 'src/types/contentImport';
import { downloadFrameworkControlsCsv } from 'src/pages/FrameworkLibrabryPage/export';
import { parseFrameworkCsv } from 'src/pages/FrameworkLibrabryPage/import';

const StyledModal = styled(Modal)`
  width: 760px;
  padding: 0 !important;
`;

const Content = styled.div`
  padding: 34px 30px 30px;
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  padding: 20px;
`;

const TemplateHint = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.cultured};
  padding: 14px 18px;

  a {
    color: ${({ theme }) => theme.colors.error};
    text-decoration: none;
    font-weight: 500;
  }
`;

const FormInput = styled(Input)`
  &[type='text'] {
    padding: 0 18px;
  }
`;

const FormTextarea = styled(Textarea)`
  min-height: 118px;
  padding: 14px 18px;
`;

const FormSelect = styled(Select)`
  width: 100%;
  min-width: 0;
`;

const UploadBox = styled.div<{ $dragActive?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme, $dragActive }) => ($dragActive ? theme.colors.stroke : theme.colors.cultured)};
  min-height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  cursor: pointer;
  transition: background 120ms ease-in-out;
`;

const SummaryCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.cultured};
  padding: 16px 18px;
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-shrink: 0;
    width: auto;
    min-width: max-content;
    padding-left: 2rem;
    padding-right: 2rem;
  }
`;

interface FrameworkOption {
  value: string;
  label: string;
}

interface PackOwnerOption {
  value: string;
  label: string;
  organisation?: unknown | null;
}

interface PackOwnerOrganisation {
  id: string;
  name: string;
  logoUrl?: string | null;
  organisationType?: string | null;
  parentId?: string | null;
}

const CYDEA_OWNER_VALUE = '__cydea__';
const normaliseOwnerId = (value?: string | null) => (value && value !== CYDEA_OWNER_VALUE ? value : null);

export interface ControlFrameworkSummaryOption {
  id: string;
  name: string;
  shortname?: string | null;
  author?: string | null;
  organisation?: PackOwnerOrganisation | null;
  description?: string | null;
  framework_identifier?: string | null;
  version?: string | null;
  publishDate?: string | null;
  frameworkControls?: Array<{
    id: string;
    control_identifier?: string | null;
    name?: string | null;
    shortname?: string | null;
    strategy?: string | null;
    description?: string | null;
  }>;
}

interface ControlFrameworkMetadataQuery {
  controlFrameworks: Array<{
    id: string;
    name: string;
    shortname?: string | null;
    author?: string | null;
    organisation?: PackOwnerOrganisation | null;
    framework_identifier?: string | null;
    version?: string | null;
    publishDate?: string | null;
    description?: string | null;
    frameworkControls?: Array<{
      id: string;
      control_identifier?: string | null;
      name?: string | null;
      shortname?: string | null;
      strategy?: string | null;
      description?: string | null;
    }>;
  }>;
}

interface ControlFrameworkMetadataQueryVariables {
  id: string;
}

const CONTROL_FRAMEWORK_METADATA = gql`
  query controlFrameworkMetadata($id: UxId) {
    controlFrameworks(id: $id) {
      id
      name
      shortname
      author
      organisation {
        id
        name
        logoUrl
        organisationType
        parentId
      }
      framework_identifier
      version
      publishDate
      description
      frameworkControls {
        id
        control_identifier
        name
        shortname
        strategy
        description
      }
    }
  }
`;

interface EditControlFrameworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdited: () => Promise<void> | void;
  frameworks: ControlFrameworkSummaryOption[];
  ownerOptions?: PackOwnerOption[];
  showOwnerSelector?: boolean;
}

export const EditControlFrameworkModal: React.FC<EditControlFrameworkModalProps> = ({
  isOpen,
  onClose,
  onEdited,
  frameworks,
  ownerOptions = [],
  showOwnerSelector = false,
}) => {
  const [selectedFrameworkId, setSelectedFrameworkId] = useState<string>('');
  const [frameworkName, setFrameworkName] = useState('');
  const [shortName, setShortName] = useState('');
  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState('');
  const [ownerOrganisationId, setOwnerOrganisationId] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [frameworkPayload, setFrameworkPayload] = useState<FrameworkImportPayload | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [createdCount, setCreatedCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState('');

  const [runUpdateMetadata, { loading: isSaving }] = useMutation<
    UpdateFrameworkMetadataMutation,
    UpdateFrameworkMetadataMutationVariables
  >(UPDATE_FRAMEWORK_METADATA);
  const [runImportFramework, { loading: isImporting }] = useMutation<ImportFrameworkMutation, ImportFrameworkMutationVariables>(
    IMPORT_FRAMEWORK,
  );

  const { data: metadataData, loading: isMetadataLoading } = useQuery<
    ControlFrameworkMetadataQuery,
    ControlFrameworkMetadataQueryVariables
  >(CONTROL_FRAMEWORK_METADATA, {
    variables: {
      id: selectedFrameworkId,
    },
    skip: !isOpen || !selectedFrameworkId,
    fetchPolicy: 'network-only',
  });

  const frameworkOptions = useMemo<FrameworkOption[]>(() => {
    return (frameworks || [])
      .map((framework) => ({
        value: framework.id,
        label: framework.shortname || framework.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [frameworks]);

  const selectedFramework = metadataData?.controlFrameworks?.[0] || frameworks.find((framework) => framework.id === selectedFrameworkId);
  const currentFrameworkControls = selectedFramework?.frameworkControls || [];

  useEffect(() => {
    if (!isOpen) return;

    const firstFramework = frameworks[0];
    setSelectedFrameworkId(firstFramework?.id || '');
    setFrameworkName('');
    setShortName('');
    setDescription('');
    setAuthor('');
    setOwnerOrganisationId(null);
    setFile(null);
    setFrameworkPayload(null);
    setValidationErrors([]);
    setCreatedCount(0);
    setUpdatedCount(0);
    setIsParsing(false);
    setError('');
  }, [isOpen, frameworks]);

  useEffect(() => {
    if (!selectedFrameworkId) return;

    const framework = metadataData?.controlFrameworks?.[0];
    if (!framework) return;
    if (framework.id !== selectedFrameworkId) return;

    setFrameworkName(framework.name || '');
    setShortName(framework.shortname || '');
    setDescription(framework.description || '');
    setAuthor(framework.author || framework.organisation?.name || 'Cydea');
    setOwnerOrganisationId(framework.organisation?.id || null);
  }, [selectedFrameworkId, metadataData]);

  const handleClose = () => {
    if (isSaving || isImporting) return;
    onClose();
  };

  const onDownloadCurrentFramework = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (!selectedFramework) return;

    downloadFrameworkControlsCsv({
      framework: {
        id: selectedFramework.id,
        framework_identifier: selectedFramework.framework_identifier,
        name: frameworkName.trim() || selectedFramework.name,
        shortname: shortName.trim() || selectedFramework.shortname,
        author: selectedAuthorName || selectedFramework.author,
        version: selectedFramework.version,
        publishDate: selectedFramework.publishDate,
        description: description.trim() || selectedFramework.description,
      },
      controls: currentFrameworkControls.map((control) => ({
        frameworkControl: control,
      })),
    });
  };

  const onDrop = async (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setIsParsing(true);
    setFrameworkPayload(null);
    setValidationErrors([]);
    setCreatedCount(0);
    setUpdatedCount(0);

    try {
      const parsed = await parseFrameworkCsv({
        file: selectedFile,
        existingControlIds: currentFrameworkControls.map((control) => control.id),
        requireFrameworkName: false,
      });

      setFrameworkPayload(parsed.payload);
      setValidationErrors(parsed.validationErrors);
      setCreatedCount(parsed.createdCount);
      setUpdatedCount(parsed.updatedCount);

      if (parsed.payload?.framework?.name && !frameworkName.trim()) setFrameworkName(parsed.payload.framework.name);
      if (parsed.payload?.framework?.shortname && !shortName.trim()) setShortName(parsed.payload.framework.shortname);
      if (parsed.payload?.framework?.description && !description.trim()) setDescription(parsed.payload.framework.description);
      if (parsed.payload?.framework?.author && !author.trim()) setAuthor(parsed.payload.framework.author);
    } finally {
      setIsParsing(false);
    }
  };

  const resetCsvState = () => {
    setFile(null);
    setFrameworkPayload(null);
    setValidationErrors([]);
    setCreatedCount(0);
    setUpdatedCount(0);
    setIsParsing(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
    disabled: isSaving || isImporting || isMetadataLoading,
  });

  const frameworkSelectValue = frameworkOptions.find((option) => option.value === selectedFrameworkId) || null;
  const selectedOwner =
    (ownerOrganisationId
      ? ownerOptions.find((option) => option.value === ownerOrganisationId)
      : author.trim() && author.trim().toLowerCase() !== 'cydea'
      ? { value: author.trim(), label: author.trim(), organisation: null }
      : null) ||
    (ownerOrganisationId && selectedFramework?.organisation
      ? {
          value: selectedFramework.organisation.id,
          label: selectedFramework.organisation.name,
          organisation: selectedFramework.organisation,
        }
      : null) ||
    ownerOptions.find((option) => option.value === CYDEA_OWNER_VALUE || option.value === '') ||
    ownerOptions[0] ||
    null;
  const selectedOwnerOrganisationId = showOwnerSelector && selectedOwner?.organisation ? normaliseOwnerId(ownerOrganisationId) : null;
  const selectedAuthorName = showOwnerSelector ? selectedOwner?.label || 'Cydea' : author.trim() || null;
  const hasValidatedFile = !!frameworkPayload && !validationErrors.length && !isParsing;
  const canSave = !!selectedFrameworkId && !!frameworkName.trim() && !isSaving && !isImporting && !isMetadataLoading;

  const onSave = async () => {
    if (!selectedFrameworkId || !frameworkName.trim()) return;

    setError('');

    try {
      if (frameworkPayload) {
        await runImportFramework({
          variables: {
            frameworkId: selectedFrameworkId,
            input: {
              ...frameworkPayload,
              framework: {
                ...frameworkPayload.framework,
                id: selectedFrameworkId,
                organisationId: selectedOwnerOrganisationId,
                name: frameworkName.trim(),
                shortname: shortName.trim() || null,
                description: description.trim() || null,
                author: selectedAuthorName,
              },
            },
          },
        });
      } else {
        await runUpdateMetadata({
          variables: {
            frameworkId: selectedFrameworkId,
            framework: {
              name: frameworkName.trim(),
              organisationId: selectedOwnerOrganisationId,
              shortname: shortName.trim() || null,
              description: description.trim() || null,
              author: selectedAuthorName,
            },
          },
        });
      }

      await onEdited();
      showSuccessToast({ title: frameworkPayload ? 'Control framework version created' : 'Control framework updated' });
      handleClose();
    } catch (err) {
      const parsedValidationErrors = parseGraphqlCsvValidationErrors(err);
      if (parsedValidationErrors.length > 0) setValidationErrors(parsedValidationErrors);
      setError(err instanceof Error ? err.message : 'Control framework update failed.');
    }
  };

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleClose} persistent={isSaving || isImporting}>
      <Content>
        <Poppins className="m-title" px={32}>
          Edit control framework
        </Poppins>

        <Spacer $px={18} />
        <Panel>
          <InputLabel>CONTROL FRAMEWORK</InputLabel>
          <Spacer $px={8} />
          <FormSelect
            options={frameworkOptions}
            value={frameworkSelectValue}
            onChange={(nextValue: unknown) => {
              setSelectedFrameworkId(((nextValue as FrameworkOption | null)?.value || '').trim());
              resetCsvState();
            }}
            placeholder="Select control framework"
            isDisabled={isSaving || isImporting || !frameworkOptions.length}
          />

          <Spacer $px={16} />
          <TemplateHint>
            <Poppins px={14}>
              <a href="#download-current-control-framework" onClick={onDownloadCurrentFramework}>
                Click here
              </a>{' '}
              to download and edit the current content of this pack in CSV.
            </Poppins>
          </TemplateHint>

          <Spacer $px={16} />
          <InputLabel>NAME</InputLabel>
          <Spacer $px={8} />
          <FormInput
            value={frameworkName}
            onChange={(e) => setFrameworkName(e.target.value)}
            placeholder="Enter control framework name"
            maxLength={255}
            disabled={!selectedFrameworkId || isSaving || isImporting || isMetadataLoading}
          />

          <Spacer $px={16} />
          <InputLabel>SHORT NAME</InputLabel>
          <Spacer $px={8} />
          <FormInput
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            placeholder="Enter short name"
            maxLength={255}
            disabled={!selectedFrameworkId || isSaving || isImporting || isMetadataLoading}
          />

          <Spacer $px={16} />
          <InputLabel>DESCRIPTION</InputLabel>
          <Spacer $px={8} />
          <FormTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this control framework"
            maxLength={3000}
            disabled={!selectedFrameworkId || isSaving || isImporting || isMetadataLoading}
          />

          <Spacer $px={16} />
          <InputLabel>AUTHOR</InputLabel>
          <Spacer $px={8} />
          {showOwnerSelector ? (
            <CreatableSelect
              options={ownerOptions}
              value={selectedOwner}
              onChange={(nextValue: unknown) => {
                const nextOwner = nextValue as PackOwnerOption | null;
                setOwnerOrganisationId(nextOwner?.organisation ? normaliseOwnerId(nextOwner?.value) : null);
                setAuthor(nextOwner?.label || 'Cydea');
              }}
              placeholder="Type in or select author"
              isDisabled={!selectedFrameworkId || isSaving || isImporting || isMetadataLoading}
              isSearchable
              formatCreateLabel={(value: string) => value}
            />
          ) : (
            <FormInput
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Enter author"
              maxLength={255}
              disabled={!selectedFrameworkId || isSaving || isImporting || isMetadataLoading}
            />
          )}

          <Spacer $px={16} />
          <InputLabel>UPLOAD UPDATED CSV FILE</InputLabel>
          <Spacer $px={8} />
          <UploadBox {...getRootProps()} $dragActive={isDragActive}>
            <input {...getInputProps()} />
            {file ? (
              <Poppins px={16} weight={500}>
                File selected: {file.name}
              </Poppins>
            ) : (
              <Poppins px={16} weight={500} color="cflowerBlue">
                {isDragActive ? 'Drop the CSV file here' : 'Click to upload CSV file'}
              </Poppins>
            )}
          </UploadBox>

          {isParsing && (
            <>
              <Spacer $px={10} />
              <Poppins px={14}>Validating CSV file...</Poppins>
            </>
          )}

          <Spacer $px={16} />
          <CsvValidationErrors errors={validationErrors} />

          {hasValidatedFile && (
            <SummaryCard>
              <SCVSummary
                items={[
                  'File validated',
                  `${createdCount + updatedCount} controls ready to be saved`,
                ]}
              />
            </SummaryCard>
          )}
        </Panel>

        {!!error && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              {error}
            </Poppins>
          </>
        )}

        {!frameworkOptions.length && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              No control frameworks available to edit.
            </Poppins>
          </>
        )}

        <Spacer $px={20} />
        <ButtonsRow>
          <Button primary onClick={onSave} disabled={!canSave} loading={isSaving || isImporting}>
            Save control framework
          </Button>
        </ButtonsRow>
      </Content>
    </StyledModal>
  );
};
